import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { paymentAPI, getErrorKey } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PhoneInput from '../components/common/PhoneInput';

const inputStyle = (hasError) => ({
  padding: '0.85rem 1rem',
  border: `1.5px solid ${hasError ? '#e53e3e' : '#e2e8f0'}`,
  borderRadius: '10px',
  fontSize: '1rem',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
});

/* ─── GUEST VIEW ─────────────────────────────────────────── */
const GuestForm = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', description: '' });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: false }));
  };

  const handleSubmit = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = true;
    if (!form.email.trim()) e.email = true;
    if (!form.phone.trim()) e.phone = true;
    if (!form.description.trim()) e.description = true;
    if (Object.keys(e).length) { setErrors(e); return; }

    navigate('/register', {
      state: {
        prefill: {
          fullName: form.fullName,
          email: form.email,
          phone: form.phone,
          pendingAdvice: { description: form.description },
        },
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
      <div>
        <label style={labelStyle}>{t('requestAdvice.fullName')} *</label>
        <input name="fullName" value={form.fullName} onChange={handleChange}
          style={inputStyle(errors.fullName)} />
      </div>
      <div>
        <label style={labelStyle}>Email *</label>
        <input name="email" type="email" value={form.email} onChange={handleChange}
          placeholder="email@example.com" style={inputStyle(errors.email)} />
      </div>
      <div>
        <label style={labelStyle}>{t('requestAdvice.phone')} *</label>
        <PhoneInput value={form.phone} onChange={val => setForm(f => ({ ...f, phone: val }))} hasError={errors.phone} />
      </div>
      <div>
        <label style={labelStyle}>{t('requestAdvice.questionLabel')} *</label>
        <textarea name="description" value={form.description} onChange={handleChange}
          placeholder={t('requestAdvice.questionPlaceholder')}
          rows={5} style={{ ...inputStyle(errors.description), resize: 'vertical', lineHeight: '1.55' }} />
      </div>
      {Object.keys(errors).length > 0 && (
        <div style={{ fontSize: '0.85rem', color: '#e53e3e' }}>{t('errors.fillAllFields')}</div>
      )}
      <button
        onClick={handleSubmit}
        style={{
          padding: '0.95rem', borderRadius: '50px', border: 'none',
          backgroundColor: '#FFD700', color: '#0d1f17',
          fontSize: '1rem', fontWeight: '700', cursor: 'pointer', marginTop: '0.25rem',
        }}
      >
        {t('requestAdvice.registerAndPay')}
      </button>
    </div>
  );
};

/* ─── PAYME QR MODAL ────────────────────────────────────────── */
const PaymeQRModal = ({ payUrl, adviceId, onClose, onSuccess }) => {
  const pollRef = useRef(null);

  useEffect(() => {
    pollRef.current = setInterval(async () => {
      try {
        const res = await paymentAPI.getStatus(adviceId);
        if (res.data.payment_status === 'paid') {
          clearInterval(pollRef.current);
          onSuccess();
        }
      } catch (_) {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [adviceId, onSuccess]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9000,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: 'white', borderRadius: '20px', padding: '2rem 2.5rem',
        width: '340px', maxWidth: '92vw', textAlign: 'center',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}>
        <img src="/payme_logo.svg" alt="Payme" style={{ height: '28px', marginBottom: '1.25rem' }} />
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.25rem' }}>
          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(payUrl)}`}
            alt="QR code"
            width={200}
            height={200}
            style={{ borderRadius: '8px' }}
          />
        </div>
        <p style={{ fontSize: '0.9rem', color: '#4a5568', marginBottom: '0.5rem', lineHeight: '1.5' }}>
          Payme иловасида QR кодни сканерланг
        </p>
        <p style={{ fontSize: '0.8rem', color: '#a0aec0', marginBottom: '1.5rem' }}>
          Тўлов автоматик аниқланади...
        </p>
        <button
          onClick={onClose}
          style={{
            width: '100%', padding: '0.75rem', borderRadius: '50px',
            border: '1.5px solid #e2e8f0', background: 'white',
            fontSize: '0.9rem', color: '#718096', cursor: 'pointer',
          }}
        >
          Бекор қилиш
        </button>
      </div>
    </div>
  );
};

/* ─── PAYMENT POPUP ─────────────────────────────────────────── */
const PaymentPopup = ({ onClose, onPayme, onClick }) => {
  const { t } = useLanguage();
  return (
  <div
    onClick={onClose}
    style={{
      position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9000,
    }}
  >
    <div
      onClick={e => e.stopPropagation()}
      style={{
        background: 'white', borderRadius: '16px', padding: '2rem',
        width: '340px', maxWidth: '92vw',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0d1f17' }}>
          {t('requestAdvice.choosePayment')}
        </h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#718096', lineHeight: 1 }}>×</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {/* Payme */}
        <button
          onClick={onPayme}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '1rem 1.25rem', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', backgroundColor: 'white',
            cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = '#0cbbbc'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(12,187,188,0.15)'; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <img src="/payme_logo.svg" alt="Payme" style={{ height: '26px', objectFit: 'contain' }} />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>

        {/* Click */}
        <button
          onClick={onClick}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '1rem 1.25rem', borderRadius: '12px',
            border: '1.5px solid #e2e8f0', backgroundColor: 'white',
            cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.borderColor = '#0065FF'; e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,101,255,0.12)'; }}
          onMouseOut={e => { e.currentTarget.style.borderColor = '#e2e8f0'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          <img src="/click_logo.svg" alt="Click" style={{ height: '22px', objectFit: 'contain' }} />
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#a0aec0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </button>
      </div>

      <p style={{ marginTop: '1.25rem', fontSize: '0.8rem', color: '#a0aec0', textAlign: 'center' }}>
        {t('requestAdvice.securePayment')}
      </p>
    </div>
  </div>
  );
};

/* ─── LOGGED-IN VIEW ─────────────────────────────────────── */
const LoggedInForm = ({ profile, prefill, pendingAdvice }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    fullName: prefill?.fullName || profile?.full_name || '',
    phone: prefill?.phone || profile?.phone || '',
    description: pendingAdvice?.description || '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [qrPayUrl, setQrPayUrl] = useState('');
  const [qrAdviceId, setQrAdviceId] = useState(null);
  const pollRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(p => ({ ...p, [name]: value }));
    setErrors(p => ({ ...p, [name]: false }));
    setError('');
  };

  const handleOpenPopup = () => {
    const e = {};
    if (!form.phone.trim()) e.phone = true;
    if (!form.description.trim()) e.description = true;
    if (Object.keys(e).length) { setErrors(e); return; }
    setShowPopup(true);
  };

  const handlePayme = async () => {
    setShowPopup(false);
    setLoading(true);
    setError('');
    try {
      const res = await paymentAPI.initiatePayme(form.description);
      const { payUrl, adviceId } = res.data;
      setQrPayUrl(payUrl);
      setQrAdviceId(adviceId);
      // Start polling
      clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        try {
          const r = await paymentAPI.getStatus(adviceId);
          if (r.data?.payment_status === 'paid') {
            clearInterval(pollRef.current);
            navigate(`/client/consultations?paid=1&aid=${adviceId}`);
          }
        } catch (_) {}
      }, 5000);
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'genericError'}`));
    } finally {
      setLoading(false);
    }
  };

  const handleClick = async () => {
    setShowPopup(false);
    setLoading(true);
    setError('');
    try {
      const res = await paymentAPI.initiateClick(form.description);
      window.location.href = res.data.payUrl;
    } catch (err) {
      setError(t(`errors.${getErrorKey(err) || 'genericError'}`));
      setLoading(false);
    }
  };

  // QR view — shown inline after Payme chosen, replaces the form
  if (qrPayUrl) {
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(qrPayUrl)}`;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.25rem', padding: '1rem 0' }}>
        <img src="/payme_logo.svg" alt="Payme" style={{ height: '28px' }} />
        <p style={{ fontSize: '0.9rem', color: '#4a5568', textAlign: 'center', fontWeight: '500' }}>
          {t('requestAdvice.scanQr')}
        </p>
        <img src={qrSrc} alt="QR" width={220} height={220}
             style={{ borderRadius: '10px', border: '1px solid #e2e8f0' }} />
        <p style={{ fontSize: '0.8rem', color: '#a0aec0', textAlign: 'center' }}>
          {t('requestAdvice.autoDetect')}
        </p>
        <button
          onClick={() => { clearInterval(pollRef.current); setQrPayUrl(''); setQrAdviceId(null); }}
          style={{ padding: '0.7rem 2rem', borderRadius: '50px', border: '1.5px solid #e2e8f0',
                   background: 'white', color: '#718096', cursor: 'pointer', fontSize: '0.9rem' }}
        >
          {t('requestAdvice.cancel')}
        </button>
      </div>
    );
  }

  return (
    <>
      {showPopup && <PaymentPopup onClose={() => setShowPopup(false)} onPayme={handlePayme} onClick={handleClick} />}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
        <div>
          <label style={labelStyle}>{t('requestAdvice.fullName')}</label>
          <input name="fullName" value={form.fullName} onChange={handleChange}
            style={inputStyle(false)} />
        </div>
        <div>
          <label style={labelStyle}>{t('requestAdvice.phone')} *</label>
          <PhoneInput value={form.phone} onChange={val => setForm(f => ({ ...f, phone: val }))} hasError={errors.phone} />
        </div>
        <div>
          <label style={labelStyle}>{t('requestAdvice.questionLabel')} *</label>
          <textarea name="description" value={form.description} onChange={handleChange}
            placeholder={t('requestAdvice.questionPlaceholder')}
            rows={5} style={{ ...inputStyle(errors.description), resize: 'vertical', lineHeight: '1.55' }} />
        </div>

        <div style={{
          backgroundColor: '#f7fafc', borderRadius: '10px', padding: '1rem 1.25rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: '0.95rem', color: '#4a5568', fontWeight: '500' }}>{t('requestAdvice.priceLabel')}</span>
          <span style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1a365d' }}>50 000 сўм</span>
        </div>

        {error && (
          <div style={{ backgroundColor: '#fed7d7', color: '#c53030', padding: '0.75rem 1rem', borderRadius: '8px', fontSize: '0.9rem' }}>
            {error}
          </div>
        )}
        {errors.description && (
          <div style={{ fontSize: '0.85rem', color: '#e53e3e' }}>{t('errors.writeQuestion')}</div>
        )}

        <button
          onClick={handleOpenPopup}
          disabled={loading}
          style={{
            padding: '0.95rem', borderRadius: '50px', border: 'none',
            backgroundColor: loading ? '#a0aec0' : '#FFD700',
            color: loading ? 'white' : '#0d1f17',
            fontSize: '1rem', fontWeight: '700', cursor: loading ? 'not-allowed' : 'pointer',
          }}
        >
          {loading ? t('requestAdvice.preparing') : t('requestAdvice.payBtn')}
        </button>
      </div>
    </>
  );
};

const labelStyle = {
  display: 'block', fontSize: '0.88rem', fontWeight: '600',
  color: '#4a5568', marginBottom: '0.4rem',
};

/* ─── MAIN PAGE ──────────────────────────────────────────── */
const RequestAdvice = () => {
  const { user, profile } = useAuth();
  const { t } = useLanguage();
  const location = useLocation();
  const { prefill, pendingAdvice } = location.state || {};

  return (
    <div style={{ maxWidth: '580px', margin: '0 auto', padding: '3rem 1.5rem' }}>
      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
        <h1 style={{ fontSize: '1.9rem', fontWeight: '800', color: '#1a365d', marginBottom: '0.5rem', lineHeight: '1.3' }}>
          {t('requestAdvice.headline')}
        </h1>
        <p style={{ color: '#718096', fontSize: '1rem' }}>
          {t('requestAdvice.headlineSub')}
        </p>
      </div>

      <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '2rem', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
        {user ? (
          <LoggedInForm profile={profile} prefill={prefill} pendingAdvice={pendingAdvice} />
        ) : (
          <GuestForm />
        )}
      </div>
    </div>
  );
};

export default RequestAdvice;
